import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

// ---------- CREATE BUILDING ----------

const createBuildingRequest = async (
  props: paths['/building']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/building']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/building']['post']['responses']['201']['content']['application/json']
    >
  >(`/building`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateBuildingMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['building'],
    mutationFn: (
      props: paths['/building']['post']['requestBody']['content']['application/json'],
    ) => createBuildingRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['buildings'] }),
  });

  return mutation;
};

// ---------- EDIT BUILDING ----------

const editBuildingRequest = async (
  props: paths['/building/{record_id}']['patch']['requestBody']['content']['application/json'] & {
    building_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/building/{record_id}']['patch']['requestBody']['content']['application/json']
  >(`/building/${props.building_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditBuildingMutation = (building_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editBuilding'],
    mutationFn: (
      props: paths['/building/{record_id}']['patch']['requestBody']['content']['application/json'] & {
        building_id: string;
      },
    ) => editBuildingRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['buildings'] }),
        client.invalidateQueries({
          queryKey: ['building', building_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- DOWNLOAD BUILDINGS CSV ----------

export const buildingsCsvDownloadRequest = async (
  props: paths['/record/buildings/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/buildings/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const useBuildingsCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['buildingCSVDownload'],
    mutationFn: (
      props: paths['/record/buildings/csv']['get']['parameters']['query'],
    ) => buildingsCsvDownloadRequest(props),
  });

  return mutation;
};

// ---------- DOWNLOAD BUILDINGS CSV TEMPLATE ----------

export const buildingsCsvTemplateDownloadRequest = async (
  record_id: string,
) => {
  const response = await axiosInstance.get(`/building/template.csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: {
      ...(record_id && { record_id }),
    },
  });

  const data = (await response).data;

  return data;
};

export const useBuildingsCsvTemplateDownloadMutation = () => {
  const mutation = useMutation({
    mutationKey: ['buildingsCSVTemplateDownload'],
    mutationFn: (record_id: string) =>
      buildingsCsvTemplateDownloadRequest(record_id),
  });

  return mutation;
};

// ---------- IMPORT BUILDINGS CSV ----------

export const buildingsCsvImportRequest = async (
  record_id: string,
  file: FormData,
) => {
  const response = await axiosInstance.post(
    `/operator/${record_id}/buildings/csv`,
    file,
    {
      headers: {
        'Content-type': 'application/xml',
      },
    },
  );

  const data = (await response).data;

  return data;
};

export const useBuildingsCsvImportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['buildingsCSVImport'],
    mutationFn: ({ file, record_id }: { file: FormData; record_id: string }) =>
      buildingsCsvImportRequest(record_id, file),
  });

  return mutation;
};
